<script lang="ts">
import * as CdsIllustrations from '@central-design-system/illustrations';
import { computed, defineComponent, unref } from 'vue';

import { classNames, pascalCase, warn } from '@components/utils';

import { makeThemeProps, makeTitleProps, provideTheme, useTitle } from '../../composable';

import type { TCdsIllustrationsName } from '@central-design-system/illustrations';
import type { PropType } from 'vue';

export const componentName = 'CdsEmptyState';
export default defineComponent({
  name: componentName,
  props: {
    /**
     * Описание
     */
    description: {
      type: String as PropType<string>,
      default: ''
    },
    /**
     * Иллюстрация
     */
    illustration: {
      type: String as PropType<TCdsIllustrationsName>,
      default: undefined,
      validator: (value: TCdsIllustrationsName) => CdsIllustrations.allowedIllustrations.includes(value)
    },
    ...makeTitleProps(),
    ...makeThemeProps()
  },
  setup(props, { slots, expose }) {
    const { hasTitle } = useTitle(props, slots);
    const { themeClasses } = provideTheme(props);

    if (!hasTitle.value) {
      warn(componentName, 'props or slot title is required');
    }

    const hasIllustrationSlot = computed(() => !!slots.illustration);
    const hasTitleSlot = computed(() => !!slots.title);
    const hasDescriptionSlot = computed(() => !!slots.description);
    const illustrationComponent = computed(() => pascalCase(props.illustration as string));

    const classes = computed(() => classNames('cds-empty-state', unref(themeClasses)));

    expose({
      illustrationComponent
    });

    return {
      illustrationComponent,
      hasIllustrationSlot,
      hasTitleSlot,
      hasDescriptionSlot,
      classes
    };
  }
});
</script>

<template>
  <div :class="classes">
    <div v-if="hasIllustrationSlot || !!illustration" class="cds-empty-state__image">
      <component :is="illustrationComponent" v-if="!!illustration" />
      <!--
        @slot Слот для изображения
        @type html|vue
      -->
      <slot v-else name="illustration" />
    </div>
    <div class="cds-empty-state__content">
      <div v-if="title || hasTitleSlot" class="cds-empty-state__title">
        <h4 v-if="title">
          {{ title }}
        </h4>
        <!--
         @slot Слот для заголовка
           @type html|vue
        -->
        <slot v-else name="title" />
      </div>

      <div v-if="description || hasDescriptionSlot" class="cds-empty-state__description">
        <span v-if="description">
          {{ description }}
        </span>
        <!--
          @slot Слот для описания
          @type html|vue
         -->
        <slot v-else name="description" />
      </div>
    </div>

    <!--
      @slot Слот для дополнительных действий
      @type html|vue
     -->
    <slot name="actions" />
  </div>
</template>

<style lang="scss">
@import './CdsEmptyState';
</style>
